import React from 'react';
import { BookOpen, ClipboardCheck, BookText, MessageCircle, ArrowRight, Play } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useSiteContent } from '../context/SiteContentContext';
import LanguageAwareVideo from '../components/common/LanguageAwareVideo';
import { buildSubtitleTracks } from '../utils/subtitles';

const iconMap = {
  0: BookOpen,
  1: ClipboardCheck,
  2: BookText,
  3: MessageCircle
};

const colorMap = {
  0: 'bg-blue-50 text-blue-600',
  1: 'bg-amber-50 text-amber-600',
  2: 'bg-purple-50 text-purple-600',
  3: 'bg-rose-50 text-rose-600'
};

const VideoCard = ({ title, description, videoUrl, subtitles, testId }) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg shadow-slate-900/5 overflow-hidden border border-slate-100">
      <div className="relative bg-slate-900">
        <LanguageAwareVideo
          videoUrl={videoUrl}
          subtitles={subtitles}
          testId={testId}
          containerClassName="bg-slate-900"
        />
      </div>
      <div className="p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
        <p className="text-slate-600 text-sm">{description}</p>
      </div>
    </div>
  );
};

const Programs = () => {
  const { siteContent } = useSiteContent();
  const programPage = siteContent?.programPage || {};
  const exampleClasses = siteContent?.exampleClasses || {};
  const items = programPage.items || [];

  return (
    <div data-testid="programs-page">
      {/* Header */}
      <section className="bg-slate-50 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center max-w-2xl mx-auto"
          >
            <span className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4 block">
              {programPage.headerLabel || 'Our Program'}
            </span>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900">
              {programPage.headerTitle || 'Program'}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Programs List */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {items.map((program, index) => {
              const Icon = iconMap[index % 4];
              const color = colorMap[index % 4];
              const programKey = program.title?.en || program.title || `program-${index}`;
              return (
                <motion.div
                  key={programKey}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.5, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="bg-slate-50 rounded-xl p-8 border border-slate-100"
                >
                  <div className="flex items-start gap-6">
                    <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">{program.title}</h3>
                      <p className="text-slate-600 leading-relaxed">{program.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Example Classes Section */}
      {exampleClasses.visible !== false && (
        <section className="py-20 md:py-28 bg-slate-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12 md:mb-16"
            >
              <div className="w-14 h-14 bg-slate-200/60 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Play className="w-6 h-6 text-slate-600" />
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
                {exampleClasses.sectionTitle || 'See Our Classes in Action'}
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                {exampleClasses.sectionSubtitle || 'Watch real examples of how we teach students of all ages'}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Kids Class */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <VideoCard
                  title={exampleClasses.kidsClass?.title || "Kids' Class Example"}
                  description={exampleClasses.kidsClass?.description || "Watch how we make learning English fun and engaging for young learners"}
                  videoUrl={exampleClasses.kidsClass?.videoUrl || "/videos/kids-class.mp4"}
                  subtitles={buildSubtitleTracks(exampleClasses.kidsClass?.subtitles)}
                  testId="kids-class-video"
                />
              </motion.div>

              {/* Adult Class */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <VideoCard
                  title={exampleClasses.adultClass?.title || "Adult Class Example"}
                  description={exampleClasses.adultClass?.description || "Experience our personalized approach to adult English education"}
                  videoUrl={exampleClasses.adultClass?.videoUrl || "/videos/adult-class.mp4"}
                  subtitles={buildSubtitleTracks(exampleClasses.adultClass?.subtitles)}
                  testId="adult-class-video"
                />
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-8">
              {programPage.ctaTitle || 'Start your learning journey today'}
            </h2>
            <Link
              to="/contact"
              data-testid="programs-cta-button"
              className="inline-flex items-center justify-center bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-3.5 font-medium transition-all"
            >
              {programPage.ctaButton || 'Get Started'}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Programs;
